import axios from 'axios'
import { useEffect, useRef } from 'react'
import { get, patch } from '../api/api'
import { currentAccountId, useAuthStore } from '../store/authStore'
import { useAppStore } from '../store/appStore'
import { sessionActions, captureRequestOrigin, isRequestCurrent } from '../store/sessionActions'
import { consumeInteractiveSignIn } from '../store/interactiveSignIn'
import { showMessage } from '../utils/snackbar'
import { UserModel } from '../models/UserModel'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { auth } from '../firebaseConfig'
import { clearAuth } from '../utils/auth'

// The whole app is gated on /me, so ride out cold starts and token propagation delays.
const ME_RETRIES = 3
const ME_RETRY_DELAY_MS = 2000

// A single extra attempt, well after get()'s own retries (~6-8s) have already been spent, before
// giving up and signing out. A brief backend blip shouldn't cost a user a full re-authentication
// (password, MFA) — this gives one that genuinely clears within half a minute a real chance to
// recover on the same still-valid credential, without leaving the session stuck indefinitely if
// it doesn't.
const GRACE_RETRY_DELAY_MS = 15_000

export const useUserInit = () => {
    const { t } = useTranslation()
    const idToken = useAuthStore((state) => state.idToken)
    const session = useAppStore((state) => state.session)
    const previousIdTokenRef = useRef<string | null>(null)
    const previousAccountIdRef = useRef<string | null>(null)
    // Set by the effect below when it signs out for a cross-account arrival during 'resolving',
    // so the /me effect can skip starting a new fetch for that same token in this same commit —
    // both effects re-run together (idToken is a dep of both), and the sign-out itself is async,
    // so without this the /me effect would still kick off a fetch for the stray account before
    // the sign-out has a chance to take effect. See the comment below for the full scenario.
    const skipResolveForCrossAccountRef = useRef(false)

    useEffect(() => {
        const previousIdToken = previousIdTokenRef.current
        const previousAccountId = previousAccountIdRef.current
        previousIdTokenRef.current = idToken
        previousAccountIdRef.current = currentAccountId()
        skipResolveForCrossAccountRef.current = false

        if (!idToken || idToken === previousIdToken) return

        const canStartResolving =
            session.tag === 'initializing' || session.tag === 'unauthenticated' || session.tag === 'accountNotFound'
        if (canStartResolving) {
            sessionActions.tokenAppeared(consumeInteractiveSignIn() ? 'login' : 'restoredSession')
            return
        }

        // A different token arrived for a *different* account while this session was already
        // 'resolving', or already resolved (active/accountDisabled) — not a same-account
        // credential renewal (currentAccountId, not the raw token, is what tells the two
        // apart). This is an abandoned sign-in attempt (an SSO popup left open, then
        // superseded by a different method) that finally resolved after a different account
        // had already taken over the session. Whichever Firebase listener applied it already
        // wrote the stray token into authStore before this effect could react, so the account
        // backing every subsequent API call has already silently moved on even though
        // session/userModel still show the account everyone else agrees on. Signing out
        // discards that stray credential outright — the alternative would be adopting it as an
        // unrequested account switch, which is no better. During 'resolving' specifically, the
        // /me effect below would otherwise just re-resolve for the new token on its own (it
        // depends on idToken too) and land 'active' for an account nobody asked for —
        // skipResolveForCrossAccountRef stops it from starting that fetch in this same commit.
        if (previousAccountId && currentAccountId() !== previousAccountId) {
            skipResolveForCrossAccountRef.current = true
            console.warn(
                '[useUserInit] Signing out: a token for a different account arrived after this session was already resolving or active'
            )
            auth.signOut().catch((signOutError) => {
                console.error('Failed to sign out after a cross-account token arrived:', signOutError)
                clearAuth()
            })
        }
    }, [idToken, session])

    useEffect(() => {
        // !idToken is redundant in practice: clearAuth() resets session to 'unauthenticated'
        // and nulls idToken in the same synchronous call, so React always batches both into one
        // render - there's no render where session is still 'resolving' but idToken is already
        // null. Checked explicitly anyway (cheap, and consistent with isStillCurrent below not
        // trusting a single signal on its own) rather than relying on that batching guarantee to
        // keep holding as this file evolves.
        if (!idToken || session.tag !== 'resolving') return
        // The other effect (same commit, since both depend on idToken) just signed out because
        // this token belongs to a different account than the one this 'resolving' session was
        // for — don't race that async sign-out by starting a fetch for the stray account here.
        if (skipResolveForCrossAccountRef.current) return
        const requestOrigin = captureRequestOrigin()

        // sessionSuperseded is set in this effect's own cleanup, which — like all passive
        // effects — React defers until after paint. A fast-resolving response's continuation
        // can run before that cleanup does, so it alone isn't a reliable guard against a newer
        // session having already replaced this one. Checking isRequestCurrent doesn't depend on
        // that timing at all, and it's account identity *and* session generation that's compared
        // (see captureRequestOrigin) — not the raw idToken (/me itself can hit a 401 and renew
        // the credential mid-request, which must not look like a different session), and not
        // account identity alone either (signing out and back in as yourself is also a different
        // session, even though the account is the same). Checking the live session tag on top
        // catches the remaining way this same request can stop being the one that matters
        // without a new session starting at all: api.ts's reportFailure can call
        // sessionActions.accountDisabled() synchronously, from this very request, before its
        // rejection ever reaches the catch block below.
        let sessionSuperseded = false
        const isStillCurrent = () =>
            !sessionSuperseded && isRequestCurrent(requestOrigin) && useAppStore.getState().session.tag === 'resolving'

        const fetchUserModel = async (isGraceRetry = false) => {
            try {
                const userModel = await get<UserModel>('/me', ME_RETRIES, ME_RETRY_DELAY_MS)
                if (!isStillCurrent()) return
                if (userModel.profile?.language) {
                    sessionActions.userResolved(userModel)
                    i18n.changeLanguage(userModel.profile.language)
                } else {
                    const language = navigator.language.split('-')[0]
                    const updated = await patch<UserModel>('/me/profile', { language })
                    if (!isStillCurrent()) return
                    sessionActions.userResolved(updated)
                    i18n.changeLanguage(updated.profile.language ?? 'en')
                }
            } catch (error) {
                if (!isStillCurrent()) return
                // Only a 404 means the account doesn't exist; other failures aren't a missing account (MD-1309).
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    sessionActions.accountNotFound()
                    auth.signOut().catch((signOutError) => {
                        console.error('Failed to sign out an accountless session:', signOutError)
                        clearAuth()
                    })
                    console.warn('Failed to fetch user info:', error)
                    return
                }

                if (!isGraceRetry) {
                    // get() has already exhausted its own retries (and, for a 401, a forced
                    // credential renewal) over the last several seconds. Rather than give up
                    // immediately, wait longer and try once more on the same still-valid
                    // credential — see GRACE_RETRY_DELAY_MS.
                    showMessage(t('errors.user_info'), 'error')
                    console.warn('Failed to fetch user info, retrying once more:', error)
                    await new Promise((resolve) => setTimeout(resolve, GRACE_RETRY_DELAY_MS))
                    if (!isStillCurrent()) return
                    await fetchUserModel(true)
                    return
                }

                // The grace retry also failed: there is no background mechanism left that could
                // still complete this on its own. Signing out returns to 'unauthenticated'
                // rather than leaving the session stuck in 'resolving' forever: a restored
                // session would be stranded on an unresponsive spinner with no affordance to
                // retry at all, and an interactive one would depend on a fresh sign-in happening
                // to mint a different token string to ever try again.
                auth.signOut().catch((signOutError) => {
                    console.error('Failed to sign out after an unrecoverable /me failure:', signOutError)
                    clearAuth()
                })
                console.warn('Failed to fetch user info after a retry:', error)
            }
        }
        fetchUserModel()
        return () => {
            sessionSuperseded = true
        }
        // idToken isn't read above, but must stay a dep: a different token can arrive (e.g. a
        // legacy-iframe postMessage for another account) while still 'resolving', and this
        // needs to supersede the in-flight fetch for that case too, not just a tag change.
    }, [idToken, session, t])
}
